import { motion, AnimatePresence } from 'framer-motion';
import { MapPin, Clock, Calendar, Share2, CalendarPlus, X, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { downloadICS } from './EventDetailModal';

const StatusBadge = ({ status }) => {
  const statusConfig = {
    active: {
      label: 'EN COURS',
      className: 'bg-emerald-500 text-white animate-pulse'
    },
    upcoming: {
      label: 'À VENIR',
      className: 'bg-[#97c7c3] text-black'
    },
    past: {
      label: 'TERMINÉ',
      className: 'bg-gray-400 text-white'
    }
  };

  const config = statusConfig[status] || statusConfig.upcoming;

  return (
    <span 
      data-testid={`status-badge-${status}`}
      className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${config.className}`}
    >
      {status === 'active' && <span className="w-2 h-2 bg-white rounded-full mr-2 animate-pulse" />}
      {config.label}
    </span>
  );
};

export const EventCard = ({ event, isHighlight = false, onClose }) => {
  if (!event) return null;

  const handleShare = async () => {
    const shareData = {
      title: `${event.name} - L'Atelier by JS`,
      text: `Retrouvez L'Atelier by JS à ${event.location} le ${event.displayDates}!`,
      url: window.location.href
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
        toast.success('Partagé avec succès!');
      } else {
        await navigator.clipboard.writeText(`${shareData.title}\n${shareData.text}\n${shareData.url}`);
        toast.success('Lien copié dans le presse-papier!');
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        toast.error('Erreur lors du partage');
      }
    }
  };

  const handleAddToCalendar = () => {
    downloadICS(event);
  };

  if (isHighlight) {
    return (
      <motion.div
        data-testid="highlight-card"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="bg-black text-white p-4 relative overflow-hidden"
      >
        {/* Gold accent line */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#baa25e] via-[#d4c078] to-[#baa25e]" />
        
        <div className="relative z-10">
          <div className="flex items-center justify-between">
            <StatusBadge status={event.status} />
            <div className="flex items-center gap-2">
              <motion.button
                data-testid="calendar-btn"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleAddToCalendar}
                className="flex items-center gap-1.5 bg-[#baa25e] text-white px-3 py-1.5 rounded-full font-dm text-xs font-bold hover:bg-[#a38d4d] transition-colors"
              >
                <CalendarPlus className="w-3 h-3" />
                Agenda
              </motion.button>
              
              <motion.button
                data-testid="share-btn"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleShare}
                className="flex items-center justify-center bg-white/10 text-white p-1.5 rounded-full font-dm text-xs font-bold hover:bg-white/20 transition-colors"
              >
                <Share2 className="w-3 h-3" />
              </motion.button>
            </div>
          </div>
          
          <h2 className="font-league text-xl uppercase tracking-wide mt-2 mb-1">
            {event.name}
          </h2>
          
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-gray-300 text-xs">
            <div className="flex items-center gap-1">
              <MapPin className="w-3 h-3 text-[#97c7c3]" />
              <span className="font-dm">{event.location}</span>
            </div>
            <div className="flex items-center gap-1">
              <Calendar className="w-3 h-3 text-[#97c7c3]" />
              <span className="font-dm">{event.displayDates}</span>
            </div>
          </div>
        </div>

        {/* Decorative element */}
        <div className="absolute -bottom-10 -right-10 w-32 h-32 bg-[#97c7c3]/10 rounded-full blur-3xl" />
      </motion.div>
    );
  }

  return (
    <motion.div
      data-testid={`event-card-${event.id}`}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 10 }}
      className="bg-white p-5 border-l-4 border-[#97c7c3] shadow-lg max-w-sm"
    >
      {onClose && (
        <button 
          data-testid="close-popup-btn"
          onClick={onClose}
          className="absolute top-2 right-2 p-1 hover:bg-gray-100 rounded-full transition-colors"
        >
          <X className="w-5 h-5 text-gray-500" />
        </button>
      )}

      <StatusBadge status={event.status} />
      
      <h3 className="font-league text-xl uppercase tracking-wide mt-3 text-black">
        {event.name}
      </h3>
      
      <div className="space-y-1.5 mt-3">
        <div className="flex items-center gap-2 text-[#666666]">
          <MapPin className="w-4 h-4 text-[#baa25e]" />
          <span className="font-dm text-sm">{event.location}</span>
        </div>
        
        <div className="flex items-center gap-2 text-[#666666]">
          <Calendar className="w-4 h-4 text-[#baa25e]" />
          <span className="font-dm text-sm">{event.displayDates}</span>
        </div>
      </div>

      <p className="font-dm text-xs text-[#666666] mt-3 leading-relaxed">
        {event.description}
      </p>

      <div className="flex items-center gap-2 mt-4">
        <motion.button
          data-testid={`popup-calendar-btn-${event.id}`}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={handleAddToCalendar}
          className="flex-1 flex items-center justify-center gap-1.5 bg-black text-white px-3 py-2 font-dm text-xs font-bold hover:bg-[#97c7c3] hover:text-black transition-colors"
        >
          <CalendarPlus className="w-3.5 h-3.5" />
          Ajouter à l'agenda
        </motion.button>
        
        <motion.button
          data-testid={`popup-share-btn-${event.id}`}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={handleShare}
          className="flex items-center justify-center gap-1.5 border border-black text-black px-3 py-2 font-dm text-xs font-bold hover:bg-black hover:text-white transition-colors"
        >
          <Share2 className="w-3.5 h-3.5" />
        </motion.button>
      </div>
    </motion.div>
  );
};

export const EventPopupContent = ({ event }) => {
  if (!event) return null;

  const handleShare = async () => {
    const shareData = {
      title: `${event.name} - L'Atelier by JS`,
      text: `Retrouvez L'Atelier by JS à ${event.location} le ${event.displayDates}!`,
      url: window.location.href
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(`${shareData.title}\n${shareData.text}\n${shareData.url}`);
        toast.success('Lien copié!');
      }
    } catch (err) {
      // Silently fail if user cancels
    }
  };

  const handleAddToCalendar = () => {
    downloadICS(event);
  };

  return (
    <div className="p-4 min-w-[280px]">
      <StatusBadge status={event.status} />
      
      <h3 className="font-league text-xl uppercase tracking-wide mt-3 text-black">
        {event.name}
      </h3>
      
      <div className="space-y-1.5 mt-3">
        <div className="flex items-center gap-2 text-[#666666]">
          <MapPin className="w-4 h-4 text-[#baa25e]" />
          <span className="font-dm text-sm">{event.address}</span>
        </div>
        
        <div className="flex items-center gap-2 text-[#666666]">
          <Calendar className="w-4 h-4 text-[#baa25e]" />
          <span className="font-dm text-sm">{event.displayDates}</span>
        </div>
      </div>

      <p className="font-dm text-xs text-[#666666] mt-3 leading-relaxed">
        {event.description}
      </p>

      <div className="flex items-center gap-2 mt-4">
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={handleAddToCalendar}
          className="flex-1 flex items-center justify-center gap-1.5 bg-black text-white px-3 py-2 font-dm text-xs font-bold hover:bg-[#97c7c3] hover:text-black transition-colors"
        >
          <CalendarPlus className="w-3.5 h-3.5" />
          Ajouter à l'agenda
        </motion.button>
        
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={handleShare}
          className="flex items-center justify-center gap-1.5 border border-black text-black px-3 py-2 font-dm text-xs font-bold hover:bg-black hover:text-white transition-colors"
        >
          <Share2 className="w-3.5 h-3.5" />
        </motion.button>
      </div>
    </div>
  );
};
